<script setup lang="ts">
import { nextTick, watch } from 'vue'
import { useMaterialComposable } from '@/components/materials/composable/useMaterialComposable'
import { ChoiceField, HelpTextTooltip, TextField } from '@haskoning/vue-component-library'
import { useCustomRockGradingStore } from '@/stores/customRockGrading'
import type { Choice } from '@haskoning/vue-component-library/dist/src/components/Form/type/type'

const { materialStore, currentItem, hasHelpText } = useMaterialComposable()
const customRockGradingStore = useCustomRockGradingStore()

const rockGradingFields = [
  { field: 'standard_rock_grading', label: 'standard' },
  { field: 'custom_rock_grading', label: 'custom' }
]

const rubbleMoundFields = [
  'rubble_mound_rock_density',
  'rubble_mound_angle_repose_rock',
  'layer_thickness_coefficient'
]

const updateCustomRockGradingChoices = () => {
  const customRockGradingChoices = customRockGradingStore.items.map(
    (item) =>
      ({
        value: item.id,
        display_name: item.name
      }) as unknown as Choice
  )

  if (materialStore.fields['custom_rock_grading']?.child) {
    materialStore.fields['custom_rock_grading'].child.choices = customRockGradingChoices
  }
}

nextTick(updateCustomRockGradingChoices)

watch(() => customRockGradingStore.items, updateCustomRockGradingChoices, { deep: true })
</script>

<template>
  <v-card elevation="0" flat>
    <v-card-title class="header-font"> Rubble mound</v-card-title>
    <v-card-subtitle class="description-font">
      Define the material conditions that will applied to all calculations in your project.
    </v-card-subtitle>

    <v-card-text>
      <span class="sub-header-font"> Rock gradings </span>
      <v-row
        class="pt-2"
        align="center"
        no-gutters
        v-for="item in rockGradingFields"
        :key="item.field"
      >
        <v-col cols="12" md="8" lg="4">
          <span>Select {{ item.label }} rock gradings to be used in your project</span>
          <ChoiceField
            :field="materialStore.fields[item.field]"
            :model-value="currentItem"
            :no-help-text="true"
            :readonly="!materialStore.canChangeMaterial"
            @update="materialStore.update(currentItem)"
          />
        </v-col>
      </v-row>

      <span class="sub-header-font"> Material densities </span>
      <v-row align="center" no-gutters v-for="field in rubbleMoundFields" :key="field">
        <v-col cols="12" md="8" lg="4" class="d-flex align-center">
          <HelpTextTooltip
            v-if="hasHelpText(field)"
            :text="materialStore.fields[field].help_text || ''"
          />
          <span :class="hasHelpText(field) ? 'px-2' : 'px-0'">
            {{ materialStore.fields[field].label }}
          </span>
        </v-col>

        <v-col cols="12" md="8" lg="4" class="pt-0">
          <TextField
            :field="materialStore.fields[field]"
            :model-value="currentItem"
            :no-help-text="true"
            :readonly="!materialStore.canChangeMaterial"
            @update="materialStore.update(currentItem)"
          />
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<style scoped></style>
